/**
 * Formats a date string for display (e.g., "Jan 1, 2026")
 * Uses the user's locale for localization
 * @param {string} dateString
 * @param {string} fallback - Value to return if date is invalid (default: '')
 * @returns {string} Formatted date string or fallback
 */
export function formatDisplayDate(dateString, fallback = '') {
  if (!dateString) return fallback;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return fallback;

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return fallback;
  }
}

/**
 * Formats a date string as localized short numeric date
 * (e.g. "01/31/26" in en-US locales).
 * @param {string} dateString
 * @param {string} fallback - Value to return if date is invalid (default: '')
 * @returns {string} Localized short date string or fallback
 */
export function formatDisplayDateShort(dateString, fallback = '') {
  if (!dateString) return fallback;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return fallback;

    return date.toLocaleDateString(undefined, {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (error) {
    return fallback;
  }
}

/**
 * Formats a date string with time for display (e.g., "Jan 1, 2026, 3:45 PM")
 * Uses the user's locale for localization
 * @param {string} dateString
 * @param {string} fallback - Value to return if date is invalid (default: '')
 * @param {Object} options
 * @param {boolean} options.includeSeconds - Whether to show seconds (default: true)
 * @param {'numeric' | '2-digit'} options.yearFormat - Year display style (default: 'numeric')
 * @returns {string} Formatted date/time string or fallback
 */
export function formatDisplayDateTime(
  dateString,
  fallback = '',
  { includeSeconds = true, yearFormat = 'numeric' } = {},
) {
  if (!dateString) return fallback;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return fallback;

    return date.toLocaleString(undefined, {
      year: yearFormat,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      ...(includeSeconds ? { second: '2-digit' } : {}),
    });
  } catch (error) {
    return fallback;
  }
}

/**
 * Formats a date string (e.g., ISO string) for use in <input type="datetime-local">
 * @param {string} dateString
 * @returns {string} Formatted date string "yyyy-MM-ddThh:mm"
 */
export function formatForDateTimeLocal(dateString) {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    return '';
  }
}
